/**
 * แก้ชื่อที่แสดงของตัวเอง
 *
 * แยกจากฟอร์มรหัสผ่านโดยสิ้นเชิง คนละ submit — การบันทึกชื่อต้องไม่ไปแตะกลไก
 * password/session และกลับกัน
 *
 * @module components/settings/ProfileNameForm
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../Input";
import { Button } from "../Button";
import { updateDisplayName } from "@/config/supabase";

export interface ProfileNameFormProps {
  firstName: string;
  lastName: string;
  disabled: boolean;
  disabledReason?: string;
}

export const ProfileNameForm: React.FC<ProfileNameFormProps> = ({
  firstName,
  lastName,
  disabled,
  disabledReason,
}) => {
  const { t } = useTranslation();
  const [first, setFirst] = useState(firstName);
  const [last, setLast] = useState(lastName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const trimmedFirst = first.trim();
  const trimmedLast = last.trim();
  const changed = trimmedFirst !== firstName.trim() || trimmedLast !== lastName.trim();
  // ชื่อว่าง = sidebar ไม่มีอะไรให้แสดง นามสกุลว่างได้ (คนไทยจำนวนมากไม่ได้กรอก)
  const valid = trimmedFirst.length > 0;

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    setBusy(true);
    const result = await updateDisplayName(trimmedFirst, trimmedLast, t);
    if (!result.success) {
      setError(result.error ?? t("settings.couldNotUpdateName"));
      setBusy(false);
      return;
    }
    setSaved(true);
    setBusy(false);
  };

  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
      <h2 className="text-base font-semibold text-ink-900">{t('settings.yourName')}</h2>

      {disabledReason && (
        <p className="text-sm text-neutral-700">{disabledReason}</p>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2 text-sm">
          {error}
        </div>
      )}
      {saved && !error && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md px-3 py-2 text-sm">
          {t('settings.nameUpdated')}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('settings.firstName')}
          value={first}
          disabled={disabled || busy}
          onChange={(e) => {
            setFirst(e.target.value);
            setError(null);
            setSaved(false);
          }}
        />
        <Input
          label={t('settings.lastName')}
          value={last}
          disabled={disabled || busy}
          onChange={(e) => {
            setLast(e.target.value);
            setError(null);
            setSaved(false);
          }}
        />
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={() => void handleSave()}
          disabled={disabled || busy || !changed || !valid}
          loading={busy}
        >
          {t('settings.saveName')}
        </Button>
      </div>
    </section>
  );
};

export default ProfileNameForm;
